import { ApiError } from './ApiError';

/**
 * How a user appears on their public cookbook and shared recipes — the name
 * and photo, and whether the cookbook is public at all.
 *
 * All of it is self-persisting like the contact email: each call writes
 * straight through, so none of it sits in the Preferences page's
 * unsaved-changes snapshot.
 */

const request = async <T>(
  path: string,
  userToken: string,
  init: RequestInit
): Promise<T> => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user${path}`, {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${userToken}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || `Request failed: ${response.status}`,
      response.status
    );
  }

  return (await response.json()) as T;
};

/** Set the display name, or clear it by passing null. */
export const updateDisplayName = async (
  displayName: string | null,
  userToken: string
): Promise<{ displayName: string | null }> =>
  request('/display-name', userToken, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayName }),
  });

/** Upload a new avatar. `imageData` is base64 with no data-URL prefix. */
export const updateProfilePhoto = async (
  imageData: string,
  contentType: string,
  userToken: string
): Promise<{ photoURL: string }> =>
  request('/profile-photo', userToken, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageData, contentType }),
  });

/** Remove the avatar. A sign-in provider's picture does not come back. */
export const removeProfilePhoto = async (
  userToken: string
): Promise<{ photoURL: null }> =>
  request('/profile-photo', userToken, { method: 'DELETE' });

/** Flip the cookbook between public and private. */
export const updateProfileVisibility = async (
  profilePublic: boolean,
  userToken: string
): Promise<{ profilePublic: boolean }> =>
  request('/profile-public', userToken, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profilePublic }),
  });
